import {isLoggedIn} from '@github-ui/client-env'
import {pullRequestUrl} from '@github-ui/issue-viewer/Urls'
import {useFeatureFlags} from '@github-ui/react-core/use-feature-flag'
import type {To} from '@github-ui/react-router'
import {ScopedCommands} from '@github-ui/ui-commands'
import type {AnalyticsEvent} from '@github-ui/use-analytics'
import React, {type ReactElement, useCallback} from 'react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import type {QUERY_FIELDS} from '../constants/queries'
import type {IssueRowSecondaryQuery$data} from './__generated__/IssueRowSecondaryQuery.graphql'
import type {PullRequestItem$key} from './__generated__/PullRequestItem.graphql'
import type {PullRequestRow$key} from './__generated__/PullRequestRow.graphql'
import {PullRequestItem} from './PullRequestItem'
import styles from './PullRequestRow.module.css'
import {usePullRequestRowSubscription} from './PullRequestRowSubscription'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const PullRequestRowSecondaryFragment = graphql`
  fragment PullRequestRowSecondary on PullRequest {
    # eslint-disable-next-line relay/must-colocate-fragment-spreads
    ...IssuePullRequestStateIconSecondary
    # eslint-disable-next-line relay/must-colocate-fragment-spreads
    ...ReviewDecision
    # eslint-disable-next-line relay/must-colocate-fragment-spreads
    ...CheckRunStatusFromPullRequest
  }
`

type Props = {
  pullRequestKey: PullRequestRow$key
  listItemMetadata?: IssueRowSecondaryQuery$data | null
  /**
   * Href getter for the metadata badge links
   * @param type - metadata type
   * @param name - name of the metadata
   * @returns URL to the metadata
   */
  getMetadataHref: (type: keyof typeof QUERY_FIELDS, metadataName: string) => string
  onSelect?: (selected: boolean) => void
  onSelectRow: (payload?: {[key: string]: unknown} | AnalyticsEvent | undefined) => void
  isActive: boolean
  isSelected?: boolean
  reactionEmojiToDisplay?: {reaction: string; reactionEmoji: string}
  sortingItemSelected: string
  additionalAnalyticsContext?: Record<string, string>
  onNavigate: (to: To) => void
  as?: React.ElementType
  role?: string
  ref?: React.Ref<HTMLAnchorElement>
}

const PullRequestRowInternal = ({
  ref,
  pullRequestKey,
  listItemMetadata,
  reactionEmojiToDisplay,
  getMetadataHref,
  onSelect,
  onSelectRow,
  isActive,
  isSelected,
  sortingItemSelected,
  additionalAnalyticsContext = {},
  onNavigate,
  ...props
}: Props): ReactElement => {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const {use_pull_request_subscriptions_enabled} = useFeatureFlags()

  const pullRequest = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment PullRequestRow on PullRequest
      @argumentDefinitions(labelPageSize: {type: "Int!"}, includeMilestone: {type: "Boolean!", defaultValue: true}) {
        __typename
        id
        repository {
          name
          owner {
            login
          }
        }
        ...PullRequestItem @arguments(labelPageSize: $labelPageSize, includeMilestone: $includeMilestone)
        number
      }
    `,
    pullRequestKey,
  )

  if (use_pull_request_subscriptions_enabled && isLoggedIn()) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    usePullRequestRowSubscription(pullRequest.id)
  }
  const pullRequestOwner = pullRequest.repository.owner.login

  const url = pullRequestUrl({owner: pullRequestOwner, repo: pullRequest.repository.name, number: pullRequest.number})

  const navigateToPR = useCallback(() => {
    onNavigate(url)
  }, [url, onNavigate])

  const handleNavigation = useCallback(
    (event: React.MouseEvent | React.KeyboardEvent) => {
      onSelectRow({
        type: pullRequest.__typename,
        ...additionalAnalyticsContext,
      })

      event.stopPropagation()
      event.preventDefault()

      navigateToPR()
    },
    [additionalAnalyticsContext, onSelectRow, pullRequest.__typename, navigateToPR],
  )

  const toggleSelection = useCallback(() => {
    onSelect?.(!isSelected)
  }, [onSelect, isSelected])

  return (
    <>
      <ScopedCommands
        commands={{
          'list-view-items-issues-prs:open-focused-item': navigateToPR,
          'list-view-items-issues-prs:toggle-focused-item-selection': toggleSelection,
        }}
        className={styles.row}
      >
        <PullRequestItem
          itemKey={pullRequest as PullRequestItem$key}
          listItemMetadata={listItemMetadata}
          isActive={isActive}
          isSelected={isSelected}
          reactionEmojiToDisplay={reactionEmojiToDisplay}
          showCommentCount
          showRepository
          showAssignees
          sortingItemSelected={sortingItemSelected}
          getMetadataHref={getMetadataHref}
          onSelect={onSelect}
          onClick={handleNavigation}
          href={url}
          ref={ref}
          {...props}
        />
      </ScopedCommands>
    </>
  )
}

PullRequestRowInternal.displayName = 'PullRequestRow'

export const PullRequestRow = React.memo(PullRequestRowInternal)
